import { useState } from "react";
import { recommend } from "@/lib/api";

const CONCERNS = ["sensitive", "rosacea", "eczema", "hormonal", "stressed", "dull", "texture", "ageing", "burnout"];

export default function TreatmentRecommender() {
  const [concerns, setConcerns] = useState([]);
  const [stress, setStress] = useState("");
  const [sensitivity, setSensitivity] = useState("");
  const [goal, setGoal] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const toggle = (c) => setConcerns((arr) => arr.includes(c) ? arr.filter(x => x !== c) : [...arr, c]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setResult("");
    try {
      const data = await recommend({ concerns, stress_level: stress, sensitivity, goal });
      setResult(data.recommendation || "");
    } catch {
      setResult("We couldn't reach the concierge just now. Please try again or send us a message via Contact.");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} data-testid="recommender" className="grid grid-cols-1 lg:grid-cols-2 gap-12">
      <div className="space-y-8">
        <div>
          <div className="label-eyebrow">What is on your skin's mind?</div>
          <div className="mt-4 flex flex-wrap gap-2">
            {CONCERNS.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`concern-${c}`}
                onClick={() => toggle(c)}
                className={`px-4 py-2 text-[12px] uppercase tracking-[0.18em] border transition-colors ${
                  concerns.includes(c)
                    ? "bg-[#2E2B27] text-[#F7F6F2] border-[#2E2B27]"
                    : "bg-transparent text-[#2E2B27] border-[#D5CDBF] hover:border-[#2E2B27]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <Select label="Stress level" value={stress} onChange={setStress} options={["low", "medium", "high"]} testid="stress" />
          <Select label="Sensitivity" value={sensitivity} onChange={setSensitivity} options={["low", "medium", "high"]} testid="sensitivity" />
        </div>

        <div>
          <div className="label-eyebrow">What are you hoping for?</div>
          <input
            data-testid="recommender-goal"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="e.g. calmer skin, less reactive, better sleep"
            className="input-soft mt-3"
          />
        </div>

        <button
          type="submit"
          data-testid="recommender-submit"
          disabled={loading}
          className="btn-primary"
        >
          {loading ? "Reading…" : "Ask Anissa"}
        </button>
      </div>

      <div className="card-edge p-8 sm:p-10 min-h-[260px] bg-[#F7F6F2]">
        <div className="label-eyebrow">A gentle suggestion</div>
        {!result && !loading && (
          <p className="serif italic mt-6 text-[22px] leading-snug text-[#6E6A63]">
            Your personalised starting point will appear here.
          </p>
        )}
        {loading && (
          <p className="serif italic mt-6 text-[20px] text-[#55524C]">Taking a moment to think for you…</p>
        )}
        {result && (
          <p data-testid="recommender-result" className="serif mt-6 text-[20px] sm:text-[22px] leading-snug text-[#2E2B27] whitespace-pre-wrap">
            {result}
          </p>
        )}
      </div>
    </form>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className="mt-3 flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            data-testid={`${testid}-${o}`}
            onClick={() => onChange(value === o ? "" : o)}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] border transition-colors ${
              value === o
                ? "bg-[#2E2B27] text-[#F7F6F2] border-[#2E2B27]"
                : "bg-transparent text-[#2E2B27] border-[#D5CDBF] hover:border-[#2E2B27]"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}
